import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTeam } from '../contexts/TeamContext';
import Layout from '../components/Layout';
import { API_URL } from '../services/api';
import { useApiHeaders } from '../hooks/useApiHeaders';

interface BotInstance {
	instance_id: string;
	agent_id: string;
	instance_name?: string;
	clawdbot_version: string;
	install_path: string;
	running_status: string;
	agent_status?: string;
	agent_hostname?: string;
}

interface ChatMessage {
	id: string;
	role: 'user' | 'assistant' | 'system';
	content: string;
	timestamp: number;
	instanceId: string;
	status?: 'sending' | 'sent' | 'error';
	blocked?: boolean;
}

interface Conversation {
	instanceId: string;
	messages: ChatMessage[];
}

interface ChatSession {
	session_id: string;
	instance_id: string;
	title: string;
	created_at: number;
	updated_at: number;
	last_message_at: number | null;
	message_count: number;
	is_archived: boolean;
}

export default function ChatPage() {
	const { firebaseUser } = useAuth();
	const { currentTeam } = useTeam();
	const { getHeaders } = useApiHeaders();
	const [instances, setInstances] = useState<BotInstance[]>([]);
	const [selectedInstance, setSelectedInstance] = useState<BotInstance | null>(null);
	const [conversations, setConversations] = useState<Map<string, Conversation>>(new Map());
	const [inputMessage, setInputMessage] = useState('');
	const [loading, setLoading] = useState(true);
	const [sending, setSending] = useState(false);
	const [sidebarOpen, setSidebarOpen] = useState(true);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const loadedSessionRef = useRef<string | null>(null); // Track loaded session to prevent loops

	// Session management state
	const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
	const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
	const [loadingSessions, setLoadingSessions] = useState(false);
	const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
	const [editingTitle, setEditingTitle] = useState('');

	// API functions for session management
	const loadSessionMessages = useCallback(async (sessionId: string) => {
		if (!firebaseUser || !selectedInstance) return;

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/chat/sessions/${sessionId}`, {
				headers: headers,
			});

			if (response.ok) {
				const data = await response.json();
				const messages = data.messages || [];

				// Convert API messages to ChatMessage format
				const chatMessages: ChatMessage[] = messages.map((msg: any) => ({
					id: msg.message_id || `${msg.timestamp}-${Math.random().toString(36).substr(2, 9)}`,
					role: msg.role,
					content: msg.content,
					timestamp: msg.timestamp,
					instanceId: selectedInstance.instance_id,
					status: 'sent' as const,
				}));

				setConversations(prev => {
					const newConversations = new Map(prev);
					newConversations.set(selectedInstance.instance_id, {
						instanceId: selectedInstance.instance_id,
						messages: chatMessages,
					});
					return newConversations;
				});
			}
		} catch (error) {
			console.error('Error loading session messages:', error);
		}
	}, [firebaseUser, selectedInstance]);

	const fetchSessions = useCallback(async (instanceId: string) => {
		if (!firebaseUser) return;

		setLoadingSessions(true);
		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/chat/sessions?instance_id=${instanceId}`, {
				headers: headers,
			});

			if (response.ok) {
				const data = await response.json();
				const sessions = data.sessions || [];
				setChatSessions(sessions.filter((s: ChatSession) => !s.is_archived));

				// If no current session selected, select the most recent one or create new
				if (!currentSessionId && sessions.length > 0) {
					const mostRecent = sessions.sort((a: ChatSession, b: ChatSession) =>
						(b.last_message_at || b.updated_at) - (a.last_message_at || a.updated_at)
					)[0];
					setCurrentSessionId(mostRecent.session_id);

					// Only load if we haven't loaded this session yet
					if (loadedSessionRef.current !== mostRecent.session_id) {
						loadedSessionRef.current = mostRecent.session_id;
						await loadSessionMessages(mostRecent.session_id);
					}
				} else if (sessions.length === 0) {
					// Create default session if none exist
					loadedSessionRef.current = null;
					// We'll create the session directly here to avoid circular dependency
					try {
						const headers = await getHeaders();
						const response = await fetch(`${API_URL}/api/v1/chat/sessions`, {
							method: 'POST',
							headers: headers,
							body: JSON.stringify({ instance_id: instanceId, title: 'New Chat' }),
						});

						if (response.ok) {
							const data = await response.json();
							// API returns session fields directly, not wrapped in {session: ...}
							const newSession: ChatSession = data.session || data;
							setChatSessions([newSession]);
							setCurrentSessionId(newSession.session_id);

							// Clear messages for new session
							setConversations(prev => {
								const newConversations = new Map(prev);
								newConversations.set(instanceId, { instanceId, messages: [] });
								return newConversations;
							});
						}
					} catch (err) {
						console.error('Error creating default session:', err);
					}
				}
			}
		} catch (error) {
			console.error('Error fetching sessions:', error);
		} finally {
			setLoadingSessions(false);
		}
	}, [firebaseUser, loadSessionMessages]);

	const createSession = useCallback(async (instanceId: string, title: string) => {
		if (!firebaseUser) return;

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/chat/sessions`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify({ instance_id: instanceId, title }),
			});

			if (response.ok) {
				const data = await response.json();
				// API returns session fields directly, not wrapped in {session: ...}
				const newSession: ChatSession = data.session || data;
				setChatSessions(prev => [newSession, ...prev]);
				setCurrentSessionId(newSession.session_id);
				loadedSessionRef.current = newSession.session_id;

				// Clear messages for new session
				setConversations(prev => {
					const newConversations = new Map(prev);
					newConversations.set(instanceId, { instanceId, messages: [] });
					return newConversations;
				});
			} else {
				const errorData = await response.json();
				console.error('Error creating session:', response.status, errorData);
				alert(`Failed to create session: ${errorData.error || 'Unknown error'}`);
			}
		} catch (error) {
			console.error('Error creating session:', error);
			alert(`Failed to create session: ${error}`);
		}
	}, [firebaseUser, currentTeam]);

	const renameSession = useCallback(async (sessionId: string, title: string) => {
		if (!firebaseUser) return;

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/chat/sessions/${sessionId}`, {
				method: 'PUT',
				headers: headers,
				body: JSON.stringify({ title }),
			});

			if (response.ok) {
				setChatSessions(prev => prev.map(s =>
					s.session_id === sessionId ? { ...s, title } : s
				));
			}
		} catch (error) {
			console.error('Error renaming session:', error);
		}
	}, [firebaseUser, currentTeam]);

	const deleteSession = useCallback(async (sessionId: string) => {
		if (!firebaseUser) return;

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/chat/sessions/${sessionId}`, {
				method: 'DELETE',
				headers: headers,
			});

			if (response.ok) {
				setChatSessions(prev => prev.filter(s => s.session_id !== sessionId));

				// If deleting current session, switch to another or create new
				if (currentSessionId === sessionId) {
					const remaining = chatSessions.filter(s => s.session_id !== sessionId);
					if (remaining.length > 0) {
						setCurrentSessionId(remaining[0].session_id);
						await loadSessionMessages(remaining[0].session_id);
					} else if (selectedInstance) {
						setCurrentSessionId(null);
						await createSession(selectedInstance.instance_id, 'New Chat');
					}
				}
			}
		} catch (error) {
			console.error('Error deleting session:', error);
		}
	}, [firebaseUser, currentSessionId, chatSessions, selectedInstance, loadSessionMessages, createSession]);

	// Reset state when team changes
	useEffect(() => {
		setSelectedInstance(null);
		setInstances([]);
		setConversations(new Map());
		setChatSessions([]);
		setCurrentSessionId(null);
	}, [currentTeam]);

	// Fetch bot instances
	useEffect(() => {
		const fetchInstances = async () => {
			if (!firebaseUser) return;

			try {
				const headers = await getHeaders();
				const response = await fetch(`${API_URL}/api/v1/instances`, {
					headers: headers,
				});

				if (response.ok) {
					const data = await response.json();
					setInstances(data.instances || []);
					// Auto-select first running instance with online agent
					const onlineInstance = data.instances?.find((i: BotInstance) =>
						i.running_status === 'running' && i.agent_status === 'online'
					);
					if (onlineInstance && !selectedInstance) {
						setSelectedInstance(onlineInstance);
					}
				}
			} catch (error) {
				console.error('Error fetching instances:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchInstances();
		// Poll for instance status updates
		const interval = setInterval(fetchInstances, 30000);
		return () => clearInterval(interval);
	}, [firebaseUser, selectedInstance, currentTeam]);

	// Load sessions when instance is selected
	useEffect(() => {
		if (selectedInstance) {
			setCurrentSessionId(null);
			setChatSessions([]);
			fetchSessions(selectedInstance.instance_id);
		}
	}, [selectedInstance?.instance_id, fetchSessions]);

	// Poll for process PAM challenges via DO endpoint (instant, no AE delay)
	const lastPAMChallengeRef = useRef<number>(Date.now());
	useEffect(() => {
		if (!firebaseUser || !selectedInstance || !currentSessionId) return;

		const pollPAMChallenges = async () => {
			try {
				const headers = await getHeaders();
				const response = await fetch(
					`${API_URL}/api/v1/chat/${selectedInstance.instance_id}/pam-challenges?since=${lastPAMChallengeRef.current}`,
					{ headers: headers },
				);

				if (response.ok) {
					const data = await response.json();
					const challenges = data.challenges || [];

					for (const challenge of challenges) {
						const challengeMsg: ChatMessage = {
							id: `pam-${challenge.challenge_id}`,
							role: 'system',
							content: `Process Access Challenge: \`${challenge.command || 'unknown command'}\` requires MFA verification.\n\nRisk: ${challenge.risk || 'unknown'} \u2014 ${challenge.description || ''}\n\nReply with \`<code> allow\` or \`<code> deny\``,
							timestamp: challenge.timestamp,
							instanceId: selectedInstance.instance_id,
							status: 'sent',
						};

						setConversations(prev => {
							const newConversations = new Map(prev);
							const conversation = newConversations.get(selectedInstance.instance_id) || {
								instanceId: selectedInstance.instance_id,
								messages: [],
							};
							// Avoid duplicate
							if (!conversation.messages.some(m => m.id === challengeMsg.id)) {
								conversation.messages = [...conversation.messages, challengeMsg];
								newConversations.set(selectedInstance.instance_id, conversation);
							}
							return newConversations;
						});
					}

					if (challenges.length > 0) {
						lastPAMChallengeRef.current = Math.max(...challenges.map((c: any) => c.timestamp));
						// Unlock input so user can type MFA response while original request is pending
						setSending(false);
					}
				}
			} catch (error) {
				// Silently ignore polling errors
			}
		};

		// Poll every 2 seconds (DO response is fast, no AE ingestion delay)
		const interval = setInterval(pollPAMChallenges, 2000);
		return () => clearInterval(interval);
	}, [firebaseUser, selectedInstance, currentSessionId]);

	// Scroll to bottom when messages change
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [conversations, selectedInstance]);

	// Get current conversation messages
	const currentMessages = selectedInstance
		? conversations.get(selectedInstance.instance_id)?.messages || []
		: [];

	// Send message to bot
	const sendMessage = useCallback(async () => {
		if (!inputMessage.trim() || !selectedInstance || !firebaseUser || sending) return;

		// Create a session if one doesn't exist yet
		if (!currentSessionId && selectedInstance) {
			try {
				await createSession(selectedInstance.instance_id, 'New Chat');
				// Wait a bit for session to be created
				await new Promise(resolve => setTimeout(resolve, 100));
			} catch (error) {
				console.log('Note: Could not create session, continuing without persistence');
				// Continue anyway - chat will work without sessions
			}
		}

		const messageContent = inputMessage.trim();
		setInputMessage('');
		setSending(true);

		const userMessage: ChatMessage = {
			id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
			role: 'user',
			content: messageContent,
			timestamp: Date.now(),
			instanceId: selectedInstance.instance_id,
			status: 'sending',
		};

		// Add user message to conversation
		setConversations(prev => {
			const newConversations = new Map(prev);
			const conversation = newConversations.get(selectedInstance.instance_id) || {
				instanceId: selectedInstance.instance_id,
				messages: [],
			};
			conversation.messages = [...conversation.messages, userMessage];
			newConversations.set(selectedInstance.instance_id, conversation);
			return newConversations;
		});

		try {
			const headers = await getHeaders();
			const response = await fetch(`${API_URL}/api/v1/chat/${selectedInstance.instance_id}/message`, {
				method: 'POST',
				headers: headers,
				body: JSON.stringify({
					message: messageContent,
					session_id: currentSessionId,
				}),
			});

			if (response.ok) {
				const data = await response.json();

				// Update user message status and add assistant/system response
				setConversations(prev => {
					const newConversations = new Map(prev);
					const conversation = newConversations.get(selectedInstance.instance_id);
					if (conversation) {
						// Update user message status
						const updatedMessages = conversation.messages.map(m =>
							m.id === userMessage.id ? { ...m, status: 'sent' as const } : m
						);

						// Add response (assistant or system for blocked messages)
						const responseMessage: ChatMessage = {
							id: data.message_id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
							role: data.blocked ? 'system' : 'assistant',
							content: data.response,
							timestamp: Date.now(),
							instanceId: selectedInstance.instance_id,
							status: 'sent',
							blocked: data.blocked || false,
						};

						conversation.messages = [...updatedMessages, responseMessage];
						newConversations.set(selectedInstance.instance_id, conversation);
					}
					return newConversations;
				});

				// Update session metadata
				setChatSessions(prev => prev.map(s =>
					s.session_id === currentSessionId
						? { ...s, last_message_at: Date.now(), message_count: s.message_count + 2 }
						: s
				));
			} else {
				// Extract error message from response
				let errorText = 'Failed to send message. Please try again.';
				try {
					const errorData = await response.json();
					if (errorData.error) errorText = errorData.error;
				} catch { /* ignore parse errors */ }

				// Mark message as error and add error response
				setConversations(prev => {
					const newConversations = new Map(prev);
					const conversation = newConversations.get(selectedInstance.instance_id);
					if (conversation) {
						const updatedMessages = conversation.messages.map(m =>
							m.id === userMessage.id ? { ...m, status: 'error' as const } : m
						);

						const errorMessage: ChatMessage = {
							id: `error-${Date.now()}`,
							role: 'system',
							content: errorText,
							timestamp: Date.now(),
							instanceId: selectedInstance.instance_id,
							status: 'sent',
						};

						conversation.messages = [...updatedMessages, errorMessage];
						newConversations.set(selectedInstance.instance_id, conversation);
					}
					return newConversations;
				});
			}
		} catch (error) {
			console.error('Error sending message:', error);
			// Mark message as error
			setConversations(prev => {
				const newConversations = new Map(prev);
				const conversation = newConversations.get(selectedInstance.instance_id);
				if (conversation) {
					conversation.messages = conversation.messages.map(m =>
						m.id === userMessage.id ? { ...m, status: 'error' as const } : m
					);
					newConversations.set(selectedInstance.instance_id, conversation);
				}
				return newConversations;
			});
		} finally {
			setSending(false);
			// Re-focus after React commits the re-render
			setTimeout(() => {
				inputRef.current?.focus();
			}, 0);
		}
	}, [inputMessage, selectedInstance, firebaseUser, sending, currentSessionId, createSession]);

	// Handle keyboard shortcuts
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			sendMessage();
		}
	};

	// Get status indicator color
	const getStatusColor = (status: string, agentStatus?: string) => {
		// If agent is offline, show gray regardless of running status
		if (agentStatus === 'offline') {
			return 'bg-slate-400';
		}

		switch (status) {
			case 'running':
				return 'bg-green-500';
			case 'stopped':
				return 'bg-slate-400';
			case 'error':
				return 'bg-red-500';
			default:
				return 'bg-amber-500';
		}
	};

	return (
		<Layout title="Chat" subtitle="Talk to your bots directly">
			<div className="flex h-[calc(100vh-12rem)] bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
				{/* Sidebar with Instances and Sessions */}
				<div className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 border-r border-slate-200 dark:border-slate-800 flex flex-col overflow-hidden`}>
					{/* Instances Section */}
					<div className="border-b border-slate-200 dark:border-slate-800">
						<div className="p-4">
							<h2 className="font-semibold text-slate-900 dark:text-white">AI Workspaces</h2>
							<p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
								Select an instance to chat with
							</p>
						</div>

						<div className="px-2 pb-2 max-h-48 overflow-y-auto">
							{loading ? (
								<div className="flex items-center justify-center py-8">
									<div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent" />
								</div>
							) : instances.length === 0 ? (
								<div className="text-center py-8 px-4">
									<svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
									</svg>
									<p className="text-sm text-slate-500 dark:text-slate-400">No AI agent instances found</p>
									<a href="/instances" className="text-sm text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block">
										Set up an instance
									</a>
								</div>
							) : (
								instances.map((instance) => (
									<button
										key={instance.instance_id}
										onClick={() => setSelectedInstance(instance)}
										className={`w-full p-3 rounded-xl text-left transition-all cursor-pointer mb-1 ${
											selectedInstance?.instance_id === instance.instance_id
												? 'bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-500'
												: 'hover:bg-slate-50 dark:hover:bg-slate-800 border-2 border-transparent'
										}`}
									>
										<div className="flex items-center space-x-3">
											<div className="relative">
												<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center text-white">
													<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
													</svg>
												</div>
												<div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 ${getStatusColor(instance.running_status, instance.agent_status)}`} />
											</div>
											<div className="flex-1 min-w-0">
												<p className="font-medium text-slate-900 dark:text-white truncate">
													{instance.instance_name || 'unnamed'}
												</p>
												<p className="text-xs text-slate-500 dark:text-slate-400 truncate">
													{instance.agent_hostname || 'Unknown Host'}
												</p>
											</div>
										</div>
									</button>
								))
							)}
						</div>
					</div>

					{/* Sessions Section */}
					{selectedInstance && (
						<div className="flex-1 flex flex-col overflow-hidden">
							<div className="p-4 border-b border-slate-200 dark:border-slate-800">
								<div className="flex items-center justify-between mb-3">
									<h2 className="font-semibold text-slate-900 dark:text-white">Chat Sessions</h2>
									<button
										onClick={() => selectedInstance && createSession(selectedInstance.instance_id, 'New Chat')}
										className="p-1.5 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 text-primary-600 dark:text-primary-400 transition-colors cursor-pointer"
										title="New Chat"
									>
										<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
										</svg>
									</button>
								</div>
							</div>

							<div className="flex-1 overflow-y-auto p-2 space-y-1">
								{loadingSessions ? (
									<div className="flex items-center justify-center py-8">
										<div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent" />
									</div>
								) : chatSessions.length === 0 ? (
									<div className="text-center py-8 px-4">
										<svg className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
										</svg>
										<p className="text-sm text-slate-500 dark:text-slate-400">No chat sessions</p>
										<button
											onClick={() => selectedInstance && createSession(selectedInstance.instance_id, 'New Chat')}
											className="text-sm text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block cursor-pointer"
										>
											Create your first chat
										</button>
									</div>
								) : (
									chatSessions
										.sort((a, b) => (b.last_message_at || b.updated_at) - (a.last_message_at || a.updated_at))
										.map((session) => (
											<div
												key={session.session_id}
												className={`p-3 rounded-xl transition-all cursor-pointer group ${
													currentSessionId === session.session_id
														? 'bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-500'
														: 'hover:bg-slate-50 dark:hover:bg-slate-800 border-2 border-transparent'
												}`}
												onClick={() => {
													setCurrentSessionId(session.session_id);
													loadSessionMessages(session.session_id);
												}}
											>
												<div className="flex items-start justify-between space-x-2">
													<div className="flex-1 min-w-0">
														{editingSessionId === session.session_id ? (
															<input
																type="text"
																value={editingTitle}
																onChange={(e) => setEditingTitle(e.target.value)}
																onBlur={() => {
																	if (editingTitle.trim()) {
																		renameSession(session.session_id, editingTitle.trim());
																	}
																	setEditingSessionId(null);
																}}
																onKeyDown={(e) => {
																	if (e.key === 'Enter') {
																		if (editingTitle.trim()) {
																			renameSession(session.session_id, editingTitle.trim());
																		}
																		setEditingSessionId(null);
																	} else if (e.key === 'Escape') {
																		setEditingSessionId(null);
																	}
																}}
																onClick={(e) => e.stopPropagation()}
																autoFocus
																className="w-full px-2 py-1 text-sm font-medium border border-primary-500 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:outline-none"
															/>
														) : (
															<p className="font-medium text-slate-900 dark:text-white truncate text-sm">
																{session.title}
															</p>
														)}
														<div className="flex items-center space-x-2 mt-1 text-xs text-slate-500 dark:text-slate-400">
															<span className="flex items-center">
																<svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
																	<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
																</svg>
																{session.message_count}
															</span>
															{session.last_message_at && (
																<span>
																	{new Date(session.last_message_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
																</span>
															)}
														</div>
													</div>
													<div className="flex items-center opacity-0 group-hover:opacity-100 transition-all">
													<button
														onClick={(e) => {
															e.stopPropagation();
															setEditingSessionId(session.session_id);
															setEditingTitle(session.title);
														}}
														className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 transition-colors cursor-pointer"
														title="Rename session"
													>
														<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
														</svg>
													</button>
													<button
														onClick={(e) => {
															e.stopPropagation();
															if (confirm('Delete this chat session?')) {
																deleteSession(session.session_id);
															}
														}}
														className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors cursor-pointer"
														title="Delete session"
													>
														<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
														</svg>
													</button>
												</div>
												</div>
											</div>
										))
								)}
							</div>
						</div>
					)}
				</div>

				{/* Chat Area */}
				<div className="flex-1 flex flex-col min-w-0">
					{/* Chat Header */}
					<div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
						<div className="flex items-center space-x-3 flex-1 min-w-0">
							<button
								onClick={() => setSidebarOpen(!sidebarOpen)}
								className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
							>
								<svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
								</svg>
							</button>
							{selectedInstance ? (
								<>
									<div className="relative flex-shrink-0">
										<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-secondary-600 flex items-center justify-center text-white">
											<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
												<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
											</svg>
										</div>
										<div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-slate-900 ${getStatusColor(selectedInstance.running_status, selectedInstance.agent_status)}`} />
									</div>
									<div className="flex-1 min-w-0">
										<h3 className="font-semibold text-slate-900 dark:text-white truncate">
											{currentSessionId ? chatSessions.find(s => s.session_id === currentSessionId)?.title || 'Chat' : selectedInstance.agent_hostname || 'Unknown Host'}
										</h3>
										<p className="text-sm text-slate-500 dark:text-slate-400 truncate">
											{selectedInstance.agent_hostname || 'Unknown Host'} • v{selectedInstance.clawdbot_version}
										</p>
									</div>
								</>
							) : (
								<div className="flex-1 min-w-0">
									<h3 className="font-semibold text-slate-900 dark:text-white">Select an AI agent</h3>
									<p className="text-sm text-slate-500 dark:text-slate-400">Choose an instance to start chatting</p>
								</div>
							)}
						</div>

						{selectedInstance && currentSessionId && (
							<div className="flex items-center space-x-2 flex-shrink-0">
								<a
									href={`/instances/${selectedInstance.instance_id}/config`}
									className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
									title="Configure"
								>
									<svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
									</svg>
								</a>
								<button
									onClick={() => {
										if (currentSessionId && confirm('Delete this chat session?')) {
											deleteSession(currentSessionId);
										}
									}}
									className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
									title="Delete session"
								>
									<svg className="w-5 h-5 text-slate-600 dark:text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
									</svg>
								</button>
							</div>
						)}
					</div>

					{/* Messages Area */}
					<div className="flex-1 overflow-y-auto p-4 space-y-4">
						{!selectedInstance ? (
							<div className="flex flex-col items-center justify-center h-full text-center">
								<div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
									<svg className="w-10 h-10 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
									</svg>
								</div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
									Start a conversation
								</h3>
								<p className="text-slate-500 dark:text-slate-400 max-w-sm">
									Select an AI agent instance from the sidebar to start chatting
								</p>
							</div>
						) : currentMessages.length === 0 ? (
							<div className="flex flex-col items-center justify-center h-full text-center">
								<div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-100 to-secondary-100 dark:from-primary-900/30 dark:to-secondary-900/30 flex items-center justify-center mb-4">
									<svg className="w-10 h-10 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
										<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
									</svg>
								</div>
								<h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
									Chat with {selectedInstance.agent_hostname || 'AI Agent'}
								</h3>
								<p className="text-slate-500 dark:text-slate-400 max-w-sm">
									Send a message to start the conversation. Your messages will be processed by the AI agent on this machine.
								</p>
							</div>
						) : (
							<>
								{currentMessages
									.filter((msg, index, arr) =>
										// Deduplicate messages with same content and role in sequence
										index === 0 ||
										msg.content !== arr[index - 1]?.content ||
										msg.role !== arr[index - 1]?.role
									)
									.map((message) => (
									<div
										key={message.id}
										className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
									>
										<div
											className={`max-w-[80%] ${
												message.role === 'user'
													? 'bg-primary-600 text-white rounded-2xl rounded-br-md'
													: message.id.startsWith('pam-')
													? 'bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-200 border border-amber-200 dark:border-amber-800 rounded-2xl rounded-bl-md'
													: message.blocked || message.role === 'system'
													? 'bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-200 border border-red-200 dark:border-red-800 rounded-2xl rounded-bl-md'
													: 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-2xl rounded-bl-md'
											} px-4 py-3`}
										>
											{message.id.startsWith('pam-') && (
												<div className="flex items-center gap-2 mb-2 text-sm font-medium text-amber-700 dark:text-amber-300">
													<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
													</svg>
													MFA Required
												</div>
											)}
											{message.blocked && !message.id.startsWith('pam-') && (
												<div className="flex items-center gap-2 mb-2 text-sm font-medium text-red-700 dark:text-red-300">
													<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
													</svg>
													Security Block
												</div>
											)}
											<p className="whitespace-pre-wrap break-words">{message.content}</p>
											<div className={`flex items-center justify-end mt-1 space-x-1 ${
												message.role === 'user' ? 'text-primary-200' : 'text-slate-400'
											}`}>
												<span className="text-xs">
													{new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
												</span>
												{message.role === 'user' && message.status === 'sending' && (
													<svg className="w-3 h-3 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
														<circle cx="10" cy="10" r="10" />
													</svg>
												)}
												{message.role === 'user' && message.status === 'sent' && (
													<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
													</svg>
												)}
												{message.role === 'user' && message.status === 'error' && (
													<svg className="w-3 h-3 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
													</svg>
												)}
											</div>
										</div>
									</div>
								))}
								<div ref={messagesEndRef} />
							</>
						)}
					</div>

					{/* Input Area */}
					{selectedInstance && (
						<div className="p-4 border-t border-slate-200 dark:border-slate-800">
							<div className="flex items-end space-x-3">
								<div className="flex-1 relative">
									<textarea
										ref={inputRef}
										value={inputMessage}
										onChange={(e) => setInputMessage(e.target.value)}
										onKeyDown={handleKeyDown}
										placeholder={selectedInstance.agent_status === 'offline'
											? "Agent is offline. Please start the agent to chat."
											: selectedInstance.running_status === 'running'
											? "Type your message..."
											: "AI agent is stopped. Start the instance to chat."
										}
										disabled={selectedInstance.agent_status === 'offline' || selectedInstance.running_status !== 'running'}
										rows={1}
										className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
										style={{ minHeight: '48px', maxHeight: '120px' }}
									/>
								</div>
								<button
									onClick={sendMessage}
									disabled={!inputMessage.trim() || selectedInstance.agent_status === 'offline' || selectedInstance.running_status !== 'running' || sending}
									className="p-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex-shrink-0"
								>
									{sending ? (
										<svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
											<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
											<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
										</svg>
									) : (
										<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
										</svg>
									)}
								</button>
							</div>
							{(selectedInstance.agent_status === 'offline' || selectedInstance.running_status !== 'running') && (
								<p className="text-sm text-amber-600 dark:text-amber-400 mt-2">
									{selectedInstance.agent_status === 'offline' ? (
										'The agent is offline. Please start the agent on your machine.'
									) : (
										<>
											This AI agent instance is not running. Start it from the{' '}
											<a href={`/instances/${selectedInstance.instance_id}/config`} className="underline">
												configuration page
											</a>{' '}
											to enable chat.
										</>
									)}
								</p>
							)}
						</div>
					)}
				</div>
			</div>
		</Layout>
	);
}
